import type { QuickloadError } from '@trello/quickload';
import {
  getQueryByOperationName,
  cacheFactory,
  getPreloadsFromInitialPath,
  waitForQuickloadUrl,
} from '@trello/quickload';

import type { DataWithId } from '@trello/graphql';
import { client, syncDeltaToCache } from '@trello/graphql';
import { sendErrorEvent } from '@trello/error-reporting';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';

/**
 * This function will grab preloads from quickload, then loop through them
 * and sync them to the apollo cache. Upon completion, it needs to mark the
 * query as being synced to the apollo cache so that queries waiting in
 * quickloadDataByDirective can resolve.
 */
export const syncQuickloadResultsToCache = async (): Promise<void> => {
  const { preloads } = getPreloadsFromInitialPath();

  await Promise.all(
    preloads.map(async ({ queryName, url, modelName }) => {
      const startTime = Date.now();
      try {
        const result = await waitForQuickloadUrl(url);

        if (!result) {
          return;
        }

        const data = result[0];

        // somehow this happens, not sure why
        // fixes an issue in sentry https://sentry.io/organizations/atlassian-2y/issues/3084762141/?project=5988847
        if (!data) {
          return;
        }

        const query = getQueryByOperationName(queryName);
        syncDeltaToCache(client, modelName, (data as unknown) as DataWithId, {
          fromDocument: query!,
        });

        Analytics.sendOperationalEvent({
          action: 'succeeded',
          actionSubject: 'quickload',
          attributes: {
            waitTime: Date.now() - startTime,
            operation: queryName,
          },
          source: getScreenFromUrl(),
        });
      } catch (err) {
        let status = 0;
        let message = null;
        if (Array.isArray(err)) {
          [status, message] = err as QuickloadError;
        }

        // no need to report on these
        if ([401, 403, 404].includes(status)) {
          return;
        }

        if (message === 'invalid token') {
          return;
        }

        sendErrorEvent(err);
        Analytics.sendOperationalEvent({
          action: 'failed',
          actionSubject: 'quickload',
          attributes: {
            waitTime: Date.now() - startTime,
            operation: queryName,
            status,
          },
          source: getScreenFromUrl(),
        });
      } finally {
        cacheFactory.markQueryHydratedFor(queryName, 'Apollo');
      }
    }),
  );
};
